import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit3, Trash2, Layout, Loader2, FileText, ChevronLeft, Search, AlertCircle, X, CheckSquare, Wand2, FileSearch, FileCheck, Download, Code, Upload, Users } from 'lucide-react';
import { supabase_get_cv_templates, supabase_delete_cv_template, supabase_get_pro_cv_templates, supabase } from '../services/supabaseService';
import { CVTemplate, User, CVSection, CVDesignConfig, CVDecoration, UserRole } from '../types';
import CVBuilder from './CVBuilder';
import ShareModal from './ShareModal';

interface CVTemplateManagerProps {
    user: User | null;
    onBack: () => void;
    onNavigate?: (tab: string, data?: any) => void;
}

// A4 dimensions for preview scaling
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

export const MiniCVPreview: React.FC<{ template: CVTemplate }> = ({ template }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.26);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            if (entries[0] && entries[0].contentRect) {
                const width = entries[0].contentRect.width;
                if (width > 0) {
                    setScale(width / 794);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
            const width = containerRef.current.getBoundingClientRect().width;
            if (width > 0) setScale(width / 794);
        }

        return () => resizeObserver.disconnect();
    }, []);

    // Only render the first page of the structure
    const pageStructure = template.structure?.[0] || [];
    const pageDecorations = template.decorations?.filter(d => d.pageIndex === 0 || d.allPages) || [];

    // Fix: Ensure design object is fully populated to avoid TS errors on possibly undefined properties
    const design: CVDesignConfig = {
        fontFamily: 'Inter, sans-serif',
        primaryColor: '#000000',
        textColor: '#000000',
        backgroundColor: '#ffffff',
        pagePadding: 40,
        itemSpacing: 20,
        fontSizeBody: 12,
        fontSizeHeading: 24,
        ...(template.design || {})
    };

    return (
        <div
            ref={containerRef}
            className="w-full h-full relative overflow-hidden bg-white"
            style={{
                backgroundColor: design.backgroundColor,
                fontFamily: design.fontFamily,
                color: design.textColor
            }}
        >
            {/* Scale content dynamically to fit parent container width */}
            <div
                className="origin-top-left absolute top-0 left-0 w-[794px] h-[1123px]"
                style={{
                    transform: `scale(${scale})`,
                }}
            >
                {/* Decorations */}
                {pageDecorations.map((deco: CVDecoration) => (
                    <div
                        key={deco.id}
                        className="absolute"
                        style={{
                            left: deco.x,
                            top: deco.y,
                            width: deco.width,
                            height: deco.height,
                            zIndex: deco.zIndex,
                            opacity: deco.opacity,
                            transform: `rotate(${deco.rotation || 0}deg)`,
                            backgroundColor: (deco.type === 'circle' || deco.type === 'square' || deco.type === 'line') ? (deco.color || design.primaryColor) : 'transparent',
                            borderRadius: deco.type === 'circle' ? '50%' : deco.type === 'rounded' ? '10px' : '0'
                        }}
                    >
                        {deco.type === 'text' && (
                            <div style={{ color: deco.color, fontSize: deco.fontSize, whiteSpace: 'pre-wrap' }}>
                                {deco.text}
                            </div>
                        )}
                        {deco.type === 'image' && deco.imageUrl && (
                            <img src={deco.imageUrl} className="w-full h-full object-cover" />
                        )}
                    </div>
                ))}

                {/* Content */}
                <div style={{ padding: `${design.pagePadding}px` }}>
                    {pageStructure.map((section: CVSection) => (
                        <div
                            key={section.id}
                            style={{
                                marginBottom: `${design.itemSpacing}px`,
                                position: (section.styles as any)?.position === 'absolute' ? 'absolute' : 'relative',
                                left: (section.styles as any)?.left,
                                top: (section.styles as any)?.top,
                                width: (section.styles as any)?.width || ((section.styles as any)?.position === 'absolute' ? 'auto' : '100%'),
                                ...section.styles
                            }}
                        >
                            {/* Simplified Render Logic for Preview */}
                            {section.type === 'header' && (
                                <div>
                                    <h1 style={{ color: design.primaryColor, fontSize: `${design.fontSizeHeading * 1.5}px`, fontWeight: 'bold' }}>{section.content?.name || 'Naam'}</h1>
                                    <p style={{ fontSize: `${design.fontSizeBody * 1.2}px`, opacity: 0.8 }}>{section.content?.role || 'Functie'}</p>
                                </div>
                            )}
                            {(section.type === 'summary' || section.type === 'custom_text') && (
                                <div>
                                    {section.title && <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5 }}>{section.title}</h3>}
                                    <p style={{ fontSize: design.fontSizeBody, whiteSpace: 'pre-wrap' }}>{section.content?.text || 'Tekst...'}</p>
                                </div>
                            )}
                            {section.type === 'experience' && (
                                <div>
                                    <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5 }}>{section.title || 'Werkervaring'}</h3>
                                    {(section.content?.items || []).slice(0, 2).map((item: any, i: number) => (
                                        <div key={i} style={{ marginBottom: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ fontWeight: 'bold', fontSize: design.fontSizeBody }}>{item.role}</span>
                                                <span style={{ fontSize: design.fontSizeBody * 0.8 }}>{item.period}</span>
                                            </div>
                                            <div style={{ fontSize: design.fontSizeBody, opacity: 0.8 }}>{item.company}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Fallback for other sections: just title */}
                            {!['header', 'summary', 'custom_text', 'experience'].includes(section.type) && section.title && (
                                <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}` }}>{section.title}</h3>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const CVTemplateManager: React.FC<CVTemplateManagerProps> = ({ user, onBack, onNavigate }) => {
    const [activeTab, setActiveTab] = useState<'easy' | 'pro'>('easy');
    const [templates, setTemplates] = useState<CVTemplate[]>([]);
    const [proTemplates, setProTemplates] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit'>('list');
    const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate | undefined>(undefined);

    // Delete Modal State
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [deleteType, setDeleteType] = useState<'easy' | 'pro'>('easy');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCreatingPro, setIsCreatingPro] = useState(false);

    // Create Pro Modal State
    const [showCreateProModal, setShowCreateProModal] = useState(false);
    const [newProTemplateName, setNewProTemplateName] = useState('Nieuw Pro Template');
    const [newProTemplateFile, setNewProTemplateFile] = useState<File | null>(null);

    // Shortcodes State
    const [showShortcodes, setShowShortcodes] = useState(false);

    // Share Modal State
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareTemplateId, setShareTemplateId] = useState<string | null>(null);
    const [shareVisibility, setShareVisibility] = useState('private');
    const [shareSharedUsers, setShareSharedUsers] = useState<string[]>([]);

    useEffect(() => {
        let mounted = true;
        const loadData = async () => {
            setIsLoading(true);
            if (!user) {
                if (mounted) setIsLoading(false);
                return;
            }
            try {
                const filterId = (user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER) ? user.clientId : undefined;
                const data = await supabase_get_cv_templates(filterId);
                const proData = await supabase_get_pro_cv_templates(user.id);
                if (mounted) {
                    if (data !== null) setTemplates(data);
                    if (proData !== null) setProTemplates(proData);
                }
            } catch (error) {
                console.error("Template fetch error:", error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        if (viewMode === 'list') {
            loadData();
        }
        return () => { mounted = false; };
    }, [user, viewMode]);

    const handleEdit = (tmpl: CVTemplate) => {
        setSelectedTemplate(tmpl);
        setViewMode('edit');
    };

    const handleCreate = () => {
        setSelectedTemplate(undefined);
        setViewMode('create');
    };

    const handleOpenCreateProModal = () => {
        setNewProTemplateName('Nieuw Pro Template');
        setNewProTemplateFile(null);
        setShowCreateProModal(true);
    };

    const handleCreatePro = async () => {
        const title = newProTemplateName.trim();
        if (!title || !user || !newProTemplateFile) {
            alert('Vul een naam in en kies een .docx bestand.');
            return;
        }
        setShowCreateProModal(false);
        setIsCreatingPro(true);
        try {
            const fileId = crypto.randomUUID();
            const storagePath = `${user.id}/cv_templates/${fileId}.docx`;

            // Upload to storage
            const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, newProTemplateFile, {
                contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                upsert: false
            });

            if (uploadError) throw uploadError;

            // Save to database
            const { data: newDoc, error: dbError } = await supabase.from('document_files').insert({
                id: fileId,
                user_id: user?.id,
                title: title,
                storage_path: storagePath,
                file_type: 'docx',
                version: 1
            }).select().single();

            if (dbError) throw dbError;

            setProTemplates(prev => [newDoc, ...prev]);

        } catch (e) {
            console.error(e);
            alert('Kon Pro template niet aanmaken.');
        } finally {
            setIsCreatingPro(false);
        }
    };

    const handleDownloadProBase = async (tmpl) => {
        try {
            const { data, error } = await supabase.storage.from('documents').createSignedUrl(tmpl.storage_path, 60);
            if (error || !data) throw error;
            window.open(data.signedUrl, '_blank');
        } catch (e) {
            console.error(e);
            alert('Bestand kon niet worden gedownload.');
        }
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        setIsDeleting(true);
        try {
            if (deleteType === 'easy') {
                await supabase_delete_cv_template(deleteId);
                setTemplates(prev => prev.filter(t => t.id !== deleteId));
            } else {
                // Delete pro template
                const { error } = await supabase.from('document_files').delete().eq('id', deleteId);
                if (error) throw error;
                setProTemplates(prev => prev.filter(t => t.id !== deleteId));
            }
        } catch (e) {
            console.error(e);
            alert("Kon template niet verwijderen.");
        } finally {
            setIsDeleting(false);
            setDeleteId(null);
        }
    };

    const handleBackFromBuilder = () => {
        setViewMode('list');
        setSelectedTemplate(undefined);
    };

    // Render the Builder View (Create/Edit) for Easy templates
    if (viewMode === 'create' || viewMode === 'edit') {
        return (
            <CVBuilder
                user={user}
                mode="design"
                initialTemplate={selectedTemplate}
                onBack={handleBackFromBuilder}
            />
        );
    }

    // Render the List View
    return (
        <>
            <div className="flex flex-col bg-slate-50 min-h-[85vh] w-full relative">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 2xl:px-8 2xl:py-6 flex flex-col shadow-sm z-20">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 2xl:mb-6">
                    <div className="mb-4 md:mb-0">
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                            <FileText className="text-[#e51b23] shrink-0" size={24} />
                            CV Templates
                        </h1>
                        <p className="text-xs text-slate-500 font-medium mt-1">Beheer branded CV ontwerpen voor je kandidaten.</p>
                    </div>
                    <div className="flex gap-3">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="px-3 py-1.5 2xl:px-4 2xl:py-2 text-slate-500 hover:text-slate-900 font-bold text-[10px] 2xl:text-xs uppercase tracking-widest transition-colors flex items-center gap-1.5 2xl:gap-2"
                            >
                                <ChevronLeft size={14} /> Terug
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-8 w-full max-w-[1920px] mx-auto">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="animate-spin text-slate-400 mb-4" size={32} />
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Templates worden geladen...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">                                <div
                                    onClick={handleOpenCreateProModal}
                                    className="bg-white border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center aspect-[210/297] cursor-pointer hover:border-red-500 hover:bg-red-50/10 transition-all group relative overflow-hidden shadow-sm hover:shadow-md"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-all"></div>
                                    {isCreatingPro ? (
                                        <Loader2 size={32} className="animate-spin text-red-600 mb-4 z-10" />
                                    ) : (
                                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center border border-slate-200 mb-4 group-hover:scale-110 group-hover:border-red-200 group-hover:bg-red-50 transition-all shadow-sm z-10">
                                            <Upload size={32} className="text-slate-400 group-hover:text-red-600" />
                                        </div>
                                    )}
                                    <p className={`text-sm font-black uppercase tracking-widest z-10 ${isCreatingPro ? 'text-red-600' : 'text-slate-500 group-hover:text-red-600'}`}>
                                        {isCreatingPro ? 'Uploaden...' : 'Upload Word Template'}
                                    </p>
                                    <p className="text-[10px] text-slate-400 font-medium mt-1 z-10 text-center px-4">Direct een .docx bestand toevoegen</p>
                                </div>

                                {proTemplates.map(tmpl => (
                                    <div key={tmpl.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden group hover:shadow-xl hover:border-slate-300 transition-all relative flex flex-col aspect-[210/297]">
                                        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-slate-50/50">
                                            <div className="w-20 h-24 bg-white shadow-sm border border-slate-200 flex flex-col group-hover:scale-110 transition-transform duration-500">
                                                <div className="h-4 bg-rose-600 w-full rounded-t-sm"></div>
                                                <div className="flex-1 flex items-center justify-center">
                                                    <FileText size={24} className="text-rose-600/50" />
                                                </div>
                                            </div>
                                            <h3 className="text-sm font-black text-slate-900 text-center mt-6 w-full truncate px-2">{tmpl.title}</h3>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-2 bg-white px-2 py-1 rounded border border-slate-200 shadow-sm flex items-center gap-1">
                                                <CheckSquare size={10} className="text-rose-600" /> Docxtemplater Ready
                                            </p>
                                        </div>
                                        <div className="p-3 bg-white border-t border-slate-100 flex justify-between items-center z-10">
                                            <span className="text-[9px] text-slate-400 font-mono">ID: {tmpl.id.slice(0, 8)}</span>
                                        </div>

                                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-sm z-20">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDownloadProBase(tmpl); }}
                                                className="w-10 h-10 bg-white text-slate-900 rounded-full flex items-center justify-center hover:bg-rose-600 hover:text-white transition-all shadow-lg transform hover:scale-110"
                                                title="Downloaden"
                                            >
                                                <Download size={18} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setDeleteId(tmpl.id); setDeleteType('pro'); }}
                                                className="w-10 h-10 bg-white text-red-600 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-lg transform hover:scale-110"
                                                title="Verwijderen"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                            <button
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    setShareTemplateId(tmpl.id);
                                                    setShareVisibility(tmpl.visibility || 'private');
                                                    setShareSharedUsers(tmpl.shared_with_users || []);
                                                    setShareModalOpen(true);
                                                }}
                                                className="w-10 h-10 bg-white text-indigo-600 rounded-full flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-all shadow-lg transform hover:scale-110"
                                                title="Delen"
                                            >
                                                <Users size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {/* Tags Guide - Now visible inside the Pro tab */}
                                <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-5 relative shadow-sm border border-slate-200 rounded-xl bg-white overflow-hidden p-8 mt-4">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner">
                                            <Code size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-black text-slate-900 text-2xl uppercase tracking-tight">Shortcodes & Tags Handleiding</h3>
                                            <p className="text-sm text-slate-500 font-medium mt-1">Typ deze shortcodes exact over in je Word documenten (.docx) om kandidaatgegevens automatisch te koppelen.</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">

                                        {/* Persona */}
                                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-[#e51b23] mb-4 pb-3 border-b border-red-200/50 flex items-center gap-2">
                                                Kandidaat Persoonsgegevens
                                            </h4>
                                            <div className="grid gap-3">
                                                {[
                                                    { tag: '[name]', desc: 'Volledige naam' },
                                                    { tag: '[voornaam]', desc: 'Voornaam kandidaat' },
                                                    { tag: '[achternaam]', desc: 'Achternaam kandidaat' },
                                                    { tag: '[email]', desc: 'E-mailadres' },
                                                    { tag: '[phone]', desc: 'Telefoonnummer' },
                                                    { tag: '[address]', desc: 'Adres / Straat' },
                                                    { tag: '[zipCode]', desc: 'Postcode' },
                                                    { tag: '[city]', desc: 'Woonplaats' },
                                                    { tag: '[country]', desc: 'Land' },
                                                    { tag: '[linkedin]', desc: 'LinkedIn profiel URL' },
                                                ].map((t, i) => (
                                                    <div key={i} className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-slate-200/60 hover:border-red-300 transition-colors shadow-sm">
                                                        <span className="text-sm text-slate-700 font-medium">{t.desc}</span>
                                                        <code className="text-[13px] font-black tracking-wide text-slate-900 bg-red-50/50 px-3 py-1 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 hover:text-red-700 transition-colors" onClick={() => navigator.clipboard.writeText(t.tag)} title="Kopiëren">
                                                            {t.tag}
                                                        </code>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-8">
                                            {/* Profile & Match */}
                                            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                                <h4 className="text-xs font-black uppercase tracking-widest text-purple-600 mb-4 pb-3 border-b border-purple-200/50 flex items-center gap-2">
                                                    Profiel & Match Elementen
                                                </h4>
                                                <div className="grid gap-3">
                                                    {[
                                                        { tag: '[role]', desc: 'Hoofdfunctie / Job Title' },
                                                        { tag: '[summary]', desc: 'Persoonlijke samenvatting' },
                                                        { tag: '[motivation]', desc: 'Aangepaste motivatie tekst' },
                                                        { tag: '[skills]', desc: 'Vaardigheden (Aparte tags, komma gescheiden)' },
                                                    ].map((t, i) => (
                                                        <div key={i} className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-slate-200/60 hover:border-purple-300 transition-colors shadow-sm">
                                                            <span className="text-sm text-slate-700 font-medium">{t.desc}</span>
                                                            <code className="text-[13px] font-black tracking-wide text-slate-900 bg-purple-50/50 px-3 py-1 rounded-lg border border-purple-100 cursor-pointer hover:bg-purple-100 hover:text-purple-700 transition-colors" onClick={() => navigator.clipboard.writeText(t.tag)} title="Kopiëren">
                                                                {t.tag}
                                                            </code>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Loops / Lists */}
                                            <div className="bg-emerald-50/30 rounded-2xl p-6 border border-emerald-100 shadow-sm relative overflow-hidden">
                                                <h4 className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-4 pb-3 border-b border-emerald-200/50 flex items-center gap-2">
                                                    Lijsten & Herhaling (Werkervaring & Opleiding)
                                                </h4>
                                                <p className="text-[13px] text-slate-600 mb-5 leading-relaxed font-medium">
                                                    Lijsten werken met zogenaamde blocks. Typ de start tag (bijv. <code className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold">[#experience]</code>) aan het begin van een sectie en de end tag <code className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold">[/experience]</code> aan het einde. Word herhaalt de styling daartussen voor alles in de lijst!
                                                </p>
                                                <div className="space-y-4">

                                                    {/* Experience Block */}
                                                    <div className="border border-emerald-200/60 rounded-xl overflow-hidden shadow-sm">
                                                        <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100/60 flex justify-between items-center cursor-pointer hover:bg-emerald-100" onClick={() => navigator.clipboard.writeText('[#experience]\n[role] bij [company] ([period])\n[description]\n[/experience]')}>
                                                            <code className="text-[13px] font-black text-emerald-800 tracking-wide">[#experience]...[/experience]</code>
                                                        </div>
                                                        <div className="p-4 bg-white grid grid-cols-2 gap-3">
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[role]')}>[role]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Functietitel</span>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[company]')}>[company]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Werkgever</span>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[period]')}>[period]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Periode</span>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[description]')}>[description]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Gedetailleerde tekst</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Education Block */}
                                                    <div className="border border-emerald-200/60 rounded-xl overflow-hidden shadow-sm">
                                                        <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100/60 flex justify-between items-center cursor-pointer hover:bg-emerald-100" onClick={() => navigator.clipboard.writeText('[#education]\n[degree] - [school] ([year])\n[/education]')}>
                                                            <code className="text-[13px] font-black text-emerald-800 tracking-wide">[#education]...[/education]</code>
                                                        </div>
                                                        <div className="p-4 bg-white grid grid-cols-2 gap-3">
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[degree]')}>[degree]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Opleiding / Niveau</span>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[school]')}>[school]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Onderwijsinstelling</span>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[year]')}>[year]</code>
                                                                <span className="text-[11px] text-slate-500 font-medium pl-1">Jaartal</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            {/* Custom Delete Confirmation Modal */}
            {deleteId && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 animate-in scale-95 duration-200">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-red-50 text-red-600 rounded-full flex items-center justify-center mb-4">
                                <Trash2 size={24} />
                            </div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-2">Template Verwijderen?</h3>
                            <p className="text-sm text-slate-500 font-medium mb-6">
                                Weet je zeker dat je dit CV template wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                            </p>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setDeleteId(null)}
                                    className="flex-1 py-3 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors"
                                >
                                    Annuleren
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={isDeleting}
                                    className="flex-1 py-3 text-white bg-red-600 hover:bg-red-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {isDeleting ? <Loader2 size={16} className="animate-spin" /> : 'Verwijderen'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Pro Template Modal */}
            {showCreateProModal && (
                <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-scaleIn">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-lg font-black text-slate-800">Nieuw Pro Template</h3>
                                <p className="text-sm text-slate-500 font-medium">Naam van het nieuwe Pro CV Template (.docx)</p>
                            </div>
                            <button onClick={() => setShowCreateProModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                                <X size={20} />
                            </button>
                        </div>
                        <input
                            type="text"
                            value={newProTemplateName}
                            onChange={(e) => setNewProTemplateName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreatePro();
                                if (e.key === 'Escape') setShowCreateProModal(false);
                            }}
                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-red-0 focus:ring-2 focus:ring-red- transition-all mb-4"
                            placeholder="Naam van het template..."
                            autoFocus
                        />
                        <div className="mb-6">
                            <label className="block text-xs font-black text-slate-800 uppercase tracking-widest mb-2">Upload Word Document (.docx) *</label>
                            <input
                                type="file"
                                accept=".docx"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        setNewProTemplateFile(e.target.files[0]);
                                    } else {
                                        setNewProTemplateFile(null);
                                    }
                                }}
                                className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-wider file:bg-rose-50 file:text-rose-600 hover:file:bg-rose-100 cursor-pointer"
                            />
                            {newProTemplateFile && <p className="text-xs text-rose-600 mt-2 font-medium">Geselecteerd: {newProTemplateFile.name}</p>}
                        </div>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => setShowCreateProModal(false)}
                                className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase tracking-wider hover:bg-slate-50 transition-colors"
                            >
                                Annuleren
                            </button>
                            <button
                                onClick={handleCreatePro}
                                disabled={!newProTemplateName.trim() || !newProTemplateFile || isCreatingPro}
                                className="flex-1 py-3 rounded-xl bg-rose-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-rose-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isCreatingPro ? <Loader2 size={16} className="animate-spin" /> : <FileCheck size={16} />}
                                Uploaden
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {shareModalOpen && shareTemplateId && (
                <ShareModal
                    isOpen={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    itemId={shareTemplateId}
                    itemType="document_files"
                    currentVisibility={shareVisibility as any}
                    currentSharedUsers={shareSharedUsers}
                    onSave={(visibility, sharedUsers) => {
                        setProTemplates(prev => prev.map(t => t.id === shareTemplateId ? { ...t, visibility, shared_with_users: sharedUsers } : t));
                    }}
                    clientId={user?.clientId}
                />
            )}
        </>
    );
};

export default CVTemplateManager;
